import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MenuItem, MenuItemClickType } from '../../main/main-menu/menu-item/menu-item';
import { OktaAuthService } from '../../../services/okta-auth.service';
import { AppNotificationCounts, SSENotificationService } from '../../../services/sse-notification.service';
import { MainMenuService } from '../../../services/main-menu.service';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { UserService } from '../../../services/user-service.service';
import { checkIsWebView } from '../../../helpers/screen-utils';
import { User } from '../../models/user.model';
import { UserProfileService } from '../../../services/user-profile.service';
import { GuardianLinkService, LinkedGuardian } from '../../../services/guardian-link.service';

import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { LanguageSwitcherComponent } from "../shared/language-switcher/language-switcher.component";
import { TranslateModule } from '@ngx-translate/core';
import { RtI18nDirective } from '../shared/runtime-translation.directive';
import { CommonModule } from '@angular/common';
import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { AiChatWidgetService } from '../shared/ai/ai-chat-widget/ai-chat-widget.service';
import { GuidedTourService } from '../shared/guided-tour.service';
import { PostsTourService } from '../posts/posts-tour.service';
import { UserImageComponent } from '../shared/user-image/user-image.component';
import { AccountSwitchService } from '../../../services/account-switch.service';

@Component({
    selector: 'apps-header',
    standalone: true,
    templateUrl: './apps-header.component.html',
    styleUrl: './apps-header.component.scss',
    imports: [CommonModule, RouterLink, RouterLinkActive, MatMenuTrigger, MatMenu, MatMenuItem, MatCheckbox, LanguageSwitcherComponent, TranslateModule, RtI18nDirective, CdkDropList, CdkDrag, UserImageComponent]
})
export class AppsHeaderComponent implements OnInit, OnDestroy {
  @Output() loadMenuItems = new EventEmitter();
  @Output() clearNotifications = new EventEmitter();
  @Output() toggleEditMode = new EventEmitter();
  @Output() unhideApp = new EventEmitter<MenuItem>();
  @Output() unhideAll = new EventEmitter();
  @Input() isEditMode: boolean = false;
  @Input() hiddenApps: MenuItem[] = [];
  @Input() isWebView: boolean = false;
  @Input() isMainMenuView: boolean = false;
  @Input() headerUserFirstName: string | undefined;

  oktaUserId: string;
  impersonatedUser: User | undefined = undefined;
  linkedGuardian: LinkedGuardian | undefined = undefined;

  /** Kept only to gate the "Clear Notifications" menu item; nav badges use appCounts. */
  newNotificationCount: number = 0;

  /** The user's nav-row apps, after Home. Role filtering already happened server-side. */
  headerApps: MenuItem[] = [];
  appCounts: AppNotificationCounts = {};
  readonly MAX_HEADER_APPS = MainMenuService.MAX_HEADER_APPS;

  /** The grid tile currently in flight (edit mode only); drives the can't-pin messaging. */
  draggingGridApp: MenuItem | null = null;

  public get canCreatePost(): boolean {
    const profile = this.userService.CurrentUserProfile;
    return (profile?.Roles.some(r => r.RoleName === 'AppAccess_Posts') ?? false) &&
           profile?.UserType === 'Staff';
  }

  isConnected = false;
  isConnecting = false;
  private subscriptions: Subscription[] = [];
  private notificationsStarted = false; // Track if SSE notifications have been initialized

  /**Show the AI toggle button oonly if they are not a student*/
  public get showAIToggle(): boolean {
    return this.userService.CurrentUserProfile?.UserType != "Student";
  }

  public get showGuidedTour(): boolean {
    return this.userService.CurrentUserProfile?.ShowGuidedTour ?? false;
  }

  /**
   * Whether to offer the Impersonate menu item. A getter rather than a stored flag because
   * oktaUserId and CurrentUserProfile arrive from independent async paths: a value snapshotted
   * when either was still missing would stay false until the page was reloaded.
   */
  public get canImpersonate(): boolean {
    if (!this.userService.CurrentUserProfile || !this.oktaUserId)
      return false;

    // A parent-account switch is an impersonation started by a staff member who need not hold any
    // impersonation role, so the "am I impersonating" fallback below would wrongly offer the menu
    // item and the API would answer 403. Returning to staff restores it.
    if (this.isParentAccountView)
      return false;

    if (this.userService.CurrentUserProfile.Roles && this.userService.CurrentUserProfile.Roles.some(x => x.RoleName === 'Impersonate' || x.RoleName === 'CanImpersonateStaff'))
      return true;

    // Mid-impersonation CurrentUserProfile carries the impersonated user's roles, so the check
    // above cannot succeed; treat an active impersonation as proof the real user was allowed to
    // start one. Approximate — see isParentAccountView for the case where it does not hold.
    if (this.oktaUserId !== this.userService.CurrentUserProfile.OktaUserId)
      return true;

    return false;
  }

  /** Staff member (not currently impersonating) who has linked their own parent account. */
  public get showSwitchToParent(): boolean {
    return this.userService.CurrentUserProfile?.UserType === 'Staff'
      && !this.impersonatedUser
      && !!this.linkedGuardian;
  }

  /** Currently viewing the app as a Guardian via impersonation -> offer switching back. */
  public get showSwitchToStaff(): boolean {
    return !!this.impersonatedUser && this.impersonatedUser.UserType === 'Guardian';
  }

  /**
   * True when the active impersonation is a staff member viewing their OWN linked parent account
   * (via "Switch to Parent account"), as opposed to a regular admin impersonation. Drives the
   * header banner to read "Parent Account" instead of "Impersonating".
   */
  public get isParentAccountView(): boolean {
    return !!this.impersonatedUser && sessionStorage.getItem('parent_account_switch') === 'true';
  }

  constructor(public userService: UserService,
              private oktaAuthService: OktaAuthService,
              private notificationService: SSENotificationService,
              private router: Router,
              private userProfileService: UserProfileService,
              private guardianLinkService: GuardianLinkService,
              private aiChatWidgetService: AiChatWidgetService,
              private guidedTourService: GuidedTourService,
              private postsTourService: PostsTourService,
              private accountSwitch: AccountSwitchService,
              private mainMenuService: MainMenuService) { }

  /** Badge count for a nav-row app, from the polling payload. */
  badgeFor(app: MenuItem): number {
    return this.appCounts[app.Id] ?? 0;
  }

  /** Keeps the guided tour's #tour-messages / #tour-posts / #tour-notifications anchors alive. */
  tourAnchorId(app: MenuItem): string | null {
    return app.ClickPath ? 'tour-' + app.ClickPath : null;
  }

  /**
   * Router target for a nav-row app. Student-type apps (Grades/Attendance) live under the
   * logged-in student's own page rather than a top-level /apps route — the same fallback
   * menu-item uses on a student's main menu. Null (no navigation) while editing.
   */
  navRoute(app: MenuItem): (string | number)[] | null {
    if (this.isEditMode) {
      return null;
    }
    if (app.ClickType === MenuItemClickType.Student) {
      const profile = this.userService.CurrentUserProfile;
      return profile?.UserType === 'Student' ? ['/apps/students', profile.Id, app.ClickPath ?? ''] : null;
    }
    return ['/apps', app.ClickPath ?? ''];
  }

  /**
   * Refuses drops the row can't take, so a full row or an app with no monochrome icon simply
   * won't accept the tile instead of silently dropping it on the floor.
   */
  canDropInHeader = (drag: CdkDrag): boolean => {
    const app = drag.data as MenuItem | undefined;
    if (!app || !MainMenuService.canPinToHeader(app)) {
      return false;
    }
    // Re-ordering something already in the row is always fine; only additions are capped.
    return this.headerApps.some(a => a.Id === app.Id)
        || this.headerApps.length < this.MAX_HEADER_APPS;
  };

  /**
   * Why the bar would refuse the tile being dragged, or null when it wouldn't. Mirrors
   * canDropInHeader, which can only silently reject — this powers the visible explanation.
   */
  get pinBlockReason(): 'not-pinnable' | 'bar-full' | null {
    const app = this.draggingGridApp;
    if (!this.isEditMode || !app) {
      return null;
    }
    if (!MainMenuService.canPinToHeader(app)) {
      return 'not-pinnable';
    }
    // Moving an app that's already in the row never needs a free slot.
    if (!this.headerApps.some(a => a.Id === app.Id) && this.headerApps.length >= this.MAX_HEADER_APPS) {
      return 'bar-full';
    }
    return null;
  }

  /** One entry per unpinned slot, so edit mode can draw a dashed box for each open spot. */
  get emptySlots(): number[] {
    return Array.from({ length: Math.max(0, this.MAX_HEADER_APPS - this.headerApps.length) }, (_, i) => i);
  }

  onHeaderDrop(event: CdkDragDrop<unknown>): void {
    const app = event.item.data as MenuItem | undefined;
    if (app) {
      // Deliberately not transferArrayItem: pinning is a copy, so the tile stays on the grid.
      this.mainMenuService.pinAppToHeader(app.Id, event.currentIndex);
    }
  }

  removeFromHeader(app: MenuItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.mainMenuService.unpinAppFromHeader(app.Id);
  }

  /** The spare-slot + : enter edit mode in place on the main menu, or head there and open it. */
  onAddAppClick(): void {
    if (this.isMainMenuView) {
      this.toggleEditMode.emit();
    } else {
      this.router.navigate(['/mainmenu'], { queryParams: { editApps: 1 } });
    }
  }
  
  async ngOnInit() {
    this.oktaAuthService.oktaUserId$.subscribe({
      next: (result) => {
        this.oktaUserId = result;
      }
    });

    // Outside the profile subscription and the isWebView guard below so the nav row paints from
    // cache immediately, on every route, without waiting for the profile to resolve.
    this.subscriptions.push(
      this.mainMenuService.headerApps$.subscribe(apps => this.headerApps = apps)
    );
    this.subscriptions.push(
      this.mainMenuService.draggingGridApp$.subscribe(app => this.draggingGridApp = app)
    );
    this.mainMenuService.ensureMenuItemsLoaded()
      .catch(error => console.error('apps-header: could not load nav row apps', error));

    this.subscriptions.push(
      this.userService.currentUserProfile$.pipe(
        filter(profile => profile !== null),
        distinctUntilChanged((prev, curr) => prev?.Id === curr?.Id)
      ).subscribe(
        (_: any) => {
          if(checkIsWebView()) {
            // Only start notifications once to avoid duplicate subscriptions
            if (!this.notificationsStarted) {
              this.startMessageNotifications();
              this.notificationsStarted = true;
            }
            // But always reconnect SSE with the new user ID when profile changes
            this.connect();
          }
          this.loadImpersonatedUser();
          this.checkLinkedGuardian();
        }
      )
    );

    // Re-check when the link is created/removed from the Profile page, so the "Switch to Parent
    // account" menu item appears/disappears without needing a full page reload.
    this.subscriptions.push(
      this.guardianLinkService.linkChanged$.subscribe(() => this.checkLinkedGuardian())
    );
  }
  

  startMessageNotifications() {
    this.subscriptions.push(
      this.notificationService.getNotifications().subscribe(async notification => {
        if (notification) {
          this.newNotificationCount = notification.NotificationCount;
        }
      }));

    this.subscriptions.push(
      this.notificationService.getAppCounts().subscribe(counts => {
        this.appCounts = counts ?? {};
      }));

    this.subscriptions.push(
      this.notificationService.getConnectionStatus().subscribe(status => {
        this.isConnected = status;
        this.isConnecting = false;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.notificationService.disconnect();
  }

  private connect(): void {
    if(!this.userService.CurrentUserProfile)
      return;

    var userId = this.userService.CurrentUserProfile!.Id;
    this.isConnecting = true;
    this.notificationService.connect(userId);
  }

  logout() {
    this.router.navigate(['logout']);
  }

  profile() {
    this.router.navigate(['/apps/profiles']);
  }

  impersonate() {
    this.router.navigate(['/impersonate']);
  }

  onMatCheckboxToggle(event: MatCheckboxChange): void {
    const isChecked = event.checked;

    if (this.userService.CurrentUserProfile) {
      const originalValue = this.userService.CurrentUserProfile.ShowAI;

      // Update the local property immediately for UI responsiveness
      this.userService.CurrentUserProfile.ShowAI = isChecked;

      // Reset widget position to default when showing the widget
      if (isChecked) {
        this.aiChatWidgetService.resetPosition();
      }

      // Call the API to persist the change
      this.userService.updateShowAIAsync(isChecked).subscribe({
        next: async (success) => {
          if (!success) {
            console.error('API returned false - reverting ShowAI setting');
            // If API call failed, revert the UI change
            this.userService.CurrentUserProfile!.ShowAI = originalValue;
          } else {
            // Force update the user profile in storage
            await this.userService.setCurrentUserProfile(this.userService.CurrentUserProfile!);
          }
        },
        error: async (error) => {
          console.error('Failed to update ShowAI setting:', error);
          // Revert the UI change on error
          this.userService.CurrentUserProfile!.ShowAI = originalValue;
        }
      });
    }
  }


    loadImpersonatedUser() {
        this.userProfileService.getImpersonatedUserProfile().subscribe(
            (result: User) => {
                // If there's an active impersonation but no session flag, this is a new browser
                // session (browser was closed). Clear the stale impersonation automatically.
                if (result && !sessionStorage.getItem('impersonation_session_active')) {
                    this.stopImpersonatingUser();
                    return;
                }
                sessionStorage.setItem('impersonation_session_active', 'true');
                this.impersonatedUser = result;
            }
        );
    }

    stopImpersonatingUser() {
        // For a parent-account switch, reload back onto the page they were on. Generic admin
        // impersonation still lands on Home, since the impersonated route may not exist for the
        // returning user.
        const returnUrl = this.isParentAccountView ? (window.location.pathname + window.location.search) : '/mainmenu';
        // Drop the banner immediately; the reload below re-initializes everything as the real user.
        this.impersonatedUser = undefined;
        this.accountSwitch.switchToStaff(returnUrl);
    }

    getImpersonatedUserName(): string {
        if (!this.impersonatedUser) return '';
        return `${this.impersonatedUser.FirstName} ${this.impersonatedUser.LastName}`;
    }

    /** Loads the staff member's linked parent account (if any) to drive the switch menu item. */
    checkLinkedGuardian() {
        if (this.userService.CurrentUserProfile?.UserType !== 'Staff') {
            this.linkedGuardian = undefined;
            return;
        }

        this.guardianLinkService.getLinkedGuardian().subscribe({
            next: (linked) => this.linkedGuardian = linked ?? undefined,
            error: () => this.linkedGuardian = undefined
        });
    }

    /** Switches the staff user into their linked guardian account via impersonation. */
    switchToParentAccount() {
        // Reload back onto the page they switched from, instead of always dumping them on Home.
        const returnUrl = window.location.pathname + window.location.search;
        this.accountSwitch.switchToParent(returnUrl);
    }

    startMainMenuTour(): void {
        this.guidedTourService.startMainMenuTour();
    }

    startProfileTour(): void {
        this.guidedTourService.startProfileTour(this.userService.CurrentUserProfile?.UserType === 'Student');
    }

    startMessageTour(): void {
        this.guidedTourService.startMessageTour();
    }

    startPostTour(): void {
        this.postsTourService.startFeedTour();
    }

    hideGuidedTour(): void {
        if (!this.userService.CurrentUserProfile) return;
        this.userService.CurrentUserProfile.ShowGuidedTour = false;
        this.userService.updateShowGuidedTourAsync(false).subscribe();
    }

    onGuidedTourToggle(event: MatCheckboxChange): void {
        const isChecked = event.checked;

        if (!this.userService.CurrentUserProfile) return;

        const originalValue = this.userService.CurrentUserProfile.ShowGuidedTour;

        // Update the local property immediately for UI responsiveness
        this.userService.CurrentUserProfile.ShowGuidedTour = isChecked;

        // Call the API to persist the change
        this.userService.updateShowGuidedTourAsync(isChecked).subscribe({
            next: async (success) => {
                if (!success) {
                    console.error('API returned false - reverting ShowGuidedTour setting');
                    this.userService.CurrentUserProfile!.ShowGuidedTour = originalValue;
                } else {
                    // Force update the user profile in storage
                    await this.userService.setCurrentUserProfile(this.userService.CurrentUserProfile!);
                }
            },
            error: async (error) => {
                console.error('Failed to update ShowGuidedTour setting:', error);
                this.userService.CurrentUserProfile!.ShowGuidedTour = originalValue;
            }
        });
    }
}
